import { useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import {
  getActiveTab,
  getCurrentUserId,
  getIsInitialUqualReferral,
  getNhDealApplicationId,
  getProspectId,
  getSelfReferralInSession,
} from "features/authSlice";
import { selectIsUqualAdBannerDismissed, setUqualAdBannerDismissed } from "features/uqualAdBannerSlice";
import { useGetDashboardItemsQuery, useCheckCreditScoreQuery } from "pages/dashboard/home/dashboardService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { UQUAL_AD_BANNER_DISMISS_KEY } from "utilities/Constant";

export const useUqualAdBannerVisibility = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const userId = useSelector(getCurrentUserId);
  const activeTab = useSelector(getActiveTab);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const prospectId = useSelector(getProspectId);
  const selfReferralInSession = useSelector(getSelfReferralInSession);
  const isInitialUqualReferral = useSelector(getIsInitialUqualReferral);
  const bannerDismissed = useSelector(selectIsUqualAdBannerDismissed);

  const isOnDashboardHome = useMemo(() => {
    const onDashboardRoot = pathname === "/dashboard" || pathname === "/dashboard/";
    const isHomeTab = activeTab === "" || activeTab === NavigationIds.Home;
    return onDashboardRoot && isHomeTab;
  }, [pathname, activeTab]);

  const { data: featureFlag } = useGetFeatureFlagsQuery();

  const { data: dashboardData } = useGetDashboardItemsQuery(
    { dealApplicationId: nhDealApplicationId, userId: userId ?? "" },
    { skip: !nhDealApplicationId || !userId },
  );

  const { data: isCreditScoreLow } = useCheckCreditScoreQuery({ nhDealApplicationId, prospectId }, { skip: !nhDealApplicationId || !prospectId });

  const isUqualSelfReferralDone = dashboardData?.isUqualSelfReferralDone;
  const isFlagOn = Boolean(featureFlag?.uQualSelfReferralAdLogicEnabled);

  // Capture isNew once when data first loads so that clearing it via setStatusFlagOfUser does not hide the banner during the same session.
  const wasNewOnLoadRef = useRef<boolean | undefined>(undefined);
  if (wasNewOnLoadRef.current === undefined && dashboardData !== undefined) {
    wasNewOnLoadRef.current = Boolean(dashboardData.isNew);
  }
  const isNew = wasNewOnLoadRef.current;

  const isEligible = useMemo((): boolean => {
    if (!isCreditScoreLow || !isFlagOn || !isNew) return false;
    if (selfReferralInSession) return isInitialUqualReferral;
    return !isUqualSelfReferralDone;
  }, [isCreditScoreLow, isFlagOn, isNew, selfReferralInSession, isInitialUqualReferral, isUqualSelfReferralDone]);

  const isBannerVisible = useMemo(() => isOnDashboardHome && isEligible && !bannerDismissed, [isOnDashboardHome, isEligible, bannerDismissed]);

  const dismissBanner = useCallback(() => {
    sessionStorage.setItem(UQUAL_AD_BANNER_DISMISS_KEY, "true");
    dispatch(setUqualAdBannerDismissed(true));
  }, [dispatch]);

  return { isBannerVisible, dismissBanner };
};
